import {
  type ChangeEvent,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import './YesNoMultiselect.scss';

import NoIcon from 'src/assets/no-icon.svg?react';
import YesIcon from 'src/assets/yes-icon.svg?react';
import { cNs } from 'src/helpers';

type CheckBoxProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export type YesNoMultiselectProps = Omit<CheckBoxProps, 'value' | 'onChange' | 'type' | 'hidden'> & {
  value?: boolean[];

  label?: string;
  description?: string | null;

  yesLabel?: string;
  noLabel?: string;

  danger?: boolean | null;

  onChange?: (checked: boolean[]) => void;
};

export function YesNoMultiselect({
  label,
  description,
  noLabel = 'No',
  yesLabel = 'Yes',
  danger,
  onChange,
  ...checkBoxProps
}: YesNoMultiselectProps) {
  const [selectedValues, setSelectedValues] = useState<boolean[]>(
    Array.isArray(checkBoxProps.value) ? checkBoxProps.value.map((item) => !!item) : []
  );

  const isYesSelected = useMemo(() => selectedValues.includes(true), [selectedValues]);
  const isNoSelected = useMemo(() => selectedValues.includes(false), [selectedValues]);

  const setValues = useCallback(
    (newValues: boolean[]) => {
      setSelectedValues(newValues);

      onChange?.(newValues);
    },
    [onChange]
  );

  const handleYesClick = useCallback(() => {
    setValues(
      selectedValues.includes(true) ? [...selectedValues.filter((item) => item !== true)] : [...selectedValues, true]
    );
  }, [selectedValues, setValues]);

  const handleNoClick = useCallback(() => {
    setValues(selectedValues.includes(false) ? [...selectedValues.filter(Boolean)] : [false, ...selectedValues]);
  }, [selectedValues, setValues]);

  const handleCheckedChange = useCallback(
    (event: ChangeEvent) => {
      const checked = (event.target as HTMLInputElement).checked;
      const value = (event.target as HTMLInputElement).value;

      if (value === 'true') {
        const hasTrue = selectedValues.includes(true);

        if (checked && !hasTrue) {
          setValues([...selectedValues, true]);
        } else if (!checked && hasTrue) {
          setValues([...selectedValues.filter((item) => item !== true)]);
        }
      } else if (value === 'false') {
        const hasFalse = selectedValues.includes(false);

        if (checked && !hasFalse) {
          setValues([false, ...selectedValues]);
        } else if (!checked && hasFalse) {
          setValues([...selectedValues.filter(Boolean)]);
        }
      }
    },
    [selectedValues, setValues]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    if (!checkBoxProps.value) {
      return;
    }

    if (checkBoxProps.value.length === 0 && selectedValues.length !== 0) {
      setSelectedValues([]);
    } else if (
      checkBoxProps.value.length !== selectedValues.length ||
      checkBoxProps.value.some((item) => !selectedValues.includes(!!item))
    ) {
      setSelectedValues(checkBoxProps.value.map((item) => !!item));
    }
  }, [checkBoxProps.value]);

  return (
    <div
      className={cNs('yes-no-multiselect__container', checkBoxProps.className, danger && 'yes-no-multiselect__danger')}
    >
      <fieldset hidden={true}>
        <input {...checkBoxProps} type="radio" hidden={true} value="true" onChange={handleCheckedChange} />
        <input {...checkBoxProps} type="radio" hidden={true} value="false" onChange={handleCheckedChange} />
      </fieldset>
      {label && <div className="yes-no-multiselect__title">{label}</div>}
      {description && <div className="yes-no-multiselect__description">{description}</div>}

      <div className="yes-no-multiselect__choices-container">
        <div
          onClick={checkBoxProps.disabled ? undefined : handleYesClick}
          className={cNs('yes-no-multiselect__label', checkBoxProps.disabled && 'yes-no-multiselect__label-disabled')}
        >
          <YesIcon className={cNs('yes-no-multiselect__icon', isYesSelected && 'yes-no-multiselect__selected')} />
          <span>{yesLabel}</span>
        </div>
        <div
          onClick={checkBoxProps.disabled ? undefined : handleNoClick}
          className={cNs('yes-no-multiselect__label', checkBoxProps.disabled && 'yes-no-multiselect__label-disabled')}
        >
          <NoIcon className={cNs('yes-no-multiselect__icon', isNoSelected && 'yes-no-multiselect__selected')} />
          <span>{noLabel}</span>
        </div>
      </div>
    </div>
  );
}
